import { z } from 'zod';

export const zUnsafeUserMetadata = z.object({
  seenRecorderTourV3: z.boolean().optional(),
  browserNotificationsEnabled: z.boolean().optional(),
});

// LEGACY: Flat entitlement field from existing PropelAuth data
// Used by existing users who subscribed through the old checkout flow
export const zOrgEntitlement = z.enum(['plan_starter', 'plan_growth', 'plan_enterprise']);

// Standard Stripe subscription statuses (V1)
export type StripeSubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

// V1 subscription status values for runtime validation
const SUBSCRIPTION_STATUS = [
  'active',
  'trialing',
  'past_due',
  'unpaid',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'paused',
] as const;

// V1 subscription schema (client-safe)
// Note: internalPlanId is treated as an opaque string on the client.
// Server-side code validates it against the strict zInternalPlanId enum
// from entitlement-plans.ts when processing webhooks and deriving entitlements.
export const zPendingSubscriptionChange = z.object({
  type: z.literal('scheduled_downgrade'),
  scheduleId: z.string(),
  scheduledPlanId: z.string(),
  scheduledPlanEffectiveAt: z.number(),
  source: z.literal('app_pending_downgrade_v1'),
});

export const zStripeSubscription = z.object({
  subscriptionId: z.string(),
  internalPlanId: z.string(),
  status: z.enum(SUBSCRIPTION_STATUS) satisfies z.ZodType<StripeSubscriptionStatus>,
  pendingChange: zPendingSubscriptionChange.optional(),
});

export const zOrgMetadata = z.object({
  // NEW: Stripe subscription data (supports multiple subscriptions)
  // An org can only have one Stripe customer, but multiple subscriptions
  stripe: z
    .object({
      customerId: z.string(),
      subscriptions: z.array(zStripeSubscription),
    })
    .optional(),

  // LEGACY: Flat fields from existing PropelAuth data - DO NOT REMOVE
  // These are used by existing users and must continue to work
  // Format: {"stripeSubscriptionId":"sub_xxx","stripeCustomerId":"cus_xxx","entitlement":"plan_starter"}
  entitlement: zOrgEntitlement.optional(),
  stripeSubscriptionId: z.string().optional(),
  stripeCustomerId: z.string().optional(),

  // Billing configuration
  billingThreshold: z.number().int().positive().optional(), // Per-org threshold in cents

  // VIP: skip payment method during checkout
  skipPaymentMethod: z.boolean().optional(),

  // Other existing fields
  hasMobileAccess: z.boolean().optional(),
  canAccessAgentV2: z.boolean().optional(),
  hasWebCodegenAgentAccess: z.boolean().optional(),
  environmentScopedVariablesEnabled: z.boolean().optional(),

  // Per-org runner limit override (for enterprise deals)
  // If set, overrides the plan-based maxRunners entitlement
  maxRunners: z.number().int().positive().optional(),

  // Per-org cloud browser limit override (for enterprise deals)
  // If set, overrides the plan-based maxBrowsers entitlement
  maxBrowsers: z.number().int().positive().optional(),

  // Codegen agent settings
  // When enabled, users must connect their GitHub account to create PRs (no bot fallback)
  requireGitHubPRAuthorship: z.boolean().optional(),

  // Max share (%) of org concurrency a single suite run can consume.
  // LEGACY: Kept for backwards compatibility. New writes use singleRunMaxWorkers.
  singleRunMaxConcurrencySharePercent: z.number().int().min(1).max(100).optional(),

  // Absolute worker cap for a single suite run.
  // Takes precedence over singleRunMaxConcurrencySharePercent when set.
  singleRunMaxWorkers: z.number().int().min(1).optional(),

  // Per-org plan display overrides (lookup keys)
  // If set, completely replaces DEFAULT_PLANS_TO_DISPLAY for this org
  // Example: ['stably-samsung-enterprise-v1'] - org only sees their custom enterprise plan
  plansToDisplay: z.array(z.string()).optional(),

  // Trial configuration (sales-gated via PropelAuth metadata)
  // canTrial: set by sales to allow org to trial any available plan
  // hasTrialed: set by checkout-completed webhook after first trial, prevents re-trial
  // hasPastPaidSubscription: prevents free trials for users who have paid in the past but downgraded
  canTrial: z.boolean().optional(),
  hasTrialed: z.boolean().optional(),
  hasPastPaidSubscription: z.boolean().optional(),

  // View mode default for the org (set at org creation time via webhook)
  // New orgs get 'agent', existing orgs have no value (treated as classic)
  defaultViewMode: z.enum(['classic', 'agent']).optional().catch(undefined),

  // Usage limit configuration (spending controls)
  // .catch(undefined) ensures malformed values don't fail the entire metadata parse
  usageLimit: z
    .object({
      // Whether to allow charges beyond included credits
      onDemandEnabled: z.boolean(),
      // Hard cap on on-demand spending in cents (null = unlimited)
      // Only enforced when onDemandEnabled is true
      // Always enforces hard stop when reached
      onDemandCapCents: z.number().int().positive().nullable(),
    })
    .optional()
    .catch(undefined),
});

export type OrgMetadata = z.output<typeof zOrgMetadata>;
export type OrgEntitlement = z.output<typeof zOrgEntitlement>;
export type StripeSubscription = z.output<typeof zStripeSubscription>;
